import React, { useEffect, useCallback, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import RecruiterTable, { COLUMNS_CONSTANTS as RECRUITER_COLUMNS } from "./recruiter/RecruiterTable";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import NotesDrawer from "bundles/Internal/components/common/NotesDrawer";
import exportCsv from "bundles/common/utils/exportCsv";
import {
  EMPTY_RECRUITER_FILTERS,
  LANGUAGE_OPTIONS,
} from "bundles/Internal/components/recruiter/RecruiterTableFilters";

function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.languages?.length) {
    filters.preferred_locale = activeFilters.languages.flatMap(
      (l) => LANGUAGE_OPTIONS.find((o) => o.value === l)?.locales || [l]
    );
    hasAny = true;
  }

  if (activeFilters.active?.length) {
    filters.active = activeFilters.active.map((v) => v === "true");
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const RecruitersIndexPage = () => {
  const [recruiters, setRecruiters] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesRecruiter, setNotesRecruiter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_RECRUITER_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const fetchRecruiters = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/recruiters", {
      params: {
        query: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setRecruiters(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch clients", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchRecruiters(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchRecruiters, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchRecruiters(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchRecruiters, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleExportCsv = useCallback((ids) => {
    exportCsv("recruiters", ids, authenticity_token, RECRUITER_COLUMNS)
      .then(() => enqueueSnackbar("CSV exported", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to export CSV", { variant: "error" }));
  }, [authenticity_token, enqueueSnackbar]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <RecruiterTable
        recruiters={recruiters}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        onRecruiterSelect={(recruiter) => {
          if (!recruiter?.id) return;
          navigate(`/internal/recruiters/${recruiter.id}`, {
            state: { recruiter, from: location.pathname },
          });
        }}
        onOpenNotes={(recruiter) => {
          setNotesRecruiter(recruiter);
          setNotesOpen(true);
        }}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectable
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        onExportCsv={handleExportCsv}
      />
      <NotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        resource="recruiters"
        record={notesRecruiter}
        onNoteAdded={(recruiterId) => {
          setRecruiters((prev) =>
            prev.map((r) =>
              String(r?.id) === String(recruiterId)
                ? { ...r, notes_count: (Number(r.notes_count) || 0) + 1 }
                : r
            )
          );
        }}
        onNoteArchived={(recruiterId) => {
          setRecruiters((prev) =>
            prev.map((r) =>
              String(r?.id) === String(recruiterId)
                ? { ...r, notes_count: Math.max((Number(r.notes_count) || 0) - 1, 0) }
                : r
            )
          );
        }}
      />
    </Box>
  );
};

export default RecruitersIndexPage;
